import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { has, isEmpty, map, values } from 'lodash';
import PipelinesList from 'js/components/pipelines/PipelinesList';
import SearchBar from 'js/components/MacroInput/SearchBar';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import ModalCloneFlowContainer from 'js/components/pipelines/ModalCloneFlowContainer';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { useFlowAdapter } from 'new/state/flows/adapter/useFlowAdapter';
import { useFlowCategoriesRepository } from 'new/state/flowCategories';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { usePipelinesRepository } from 'new/state/pipelines/repository/usePipelinesRepository';
import usePaging from 'new/state/paging/repository/usePaging';
import { refreshPipeline } from 'js/actions/pipelines';
import Constants from 'js/Constants';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import { selectStyles } from './selectStyles';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import {
  ASSIGN_FLOWS_TO_PROJECT_REQUEST,
  GET_PIPELINES_REQUEST,
  REFRESH_PIPELINE_REQUEST,
} from 'js/constants/actionTypes';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { useAccessControl, useAppRepository } from 'new/state/app/repository/useAppRepository';
import { useUncategorizedPipelinesState } from 'new/components/pipelines/UncategorizedPipelines/hooks/useUncategorizedPipelinesState';
import { useUncategorizedPipelinesEntities } from 'new/components/pipelines/UncategorizedPipelines/hooks/useUncategorizedPipelinesEntities';
import { useSelectedResourceIds } from 'new/components/pipelines/UncategorizedPipelines/hooks/useSelectedResourceIds';
import { useAutoRedirectOnEmptyPage } from 'new/components/pipelines/UncategorizedPipelines/hooks/useAutoRedirectOnEmptyPage';
import { BulkActionsBar } from 'new/components/pipelines/bulkActions';
import useURLSearchParams from 'new/hooks/useURLSearchParams';

const sortCriteriaOptions = values(Constants.SortCriteriaInfo);

const ITEMS_PER_PAGE = Constants.LISTING_ITEMS_PER_PAGE;
const SEARCH_VIEW = SearchFilterViews.PROJECT_PIPELINES;
const AVAILABLE_TYPES = AvailableFilterTypesByView[SearchFilterViews.PROJECT_PIPELINES];

const UncategorizedPipelines: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { metaInfoById: pagingMetaInfo } = usePaging();
  const { mode: accessControlMode } = useAccessControl();
  const { requestFlowsWithoutProjects, cleanupFlowsWithoutProjects } = useFlowAdapter();

  const { visibilityFilters } = useAppRepository();
  const { pipelines: pipelinesState } = usePipelinesRepository();
  const { getFlowsWithoutProjects } = useFlowCategoriesRepository();

  const { entities, sources, vendors, flowNodes } = useUncategorizedPipelinesEntities();
  const isLoadingPipelines = useIsLoading(GET_PIPELINES_REQUEST);
  const isRefreshingPipeline = useIsLoading(REFRESH_PIPELINE_REQUEST);
  const isAssigningLoading = useIsLoading(ASSIGN_FLOWS_TO_PROJECT_REQUEST);

  const selectedCardID = pipelinesState.detailView?.id;
  const { ids: flowIds, loading, meta } = getFlowsWithoutProjects();
  const filters = visibilityFilters.filters[SEARCH_VIEW];
  const { params } = useURLSearchParams('page');

  const initialPage = Number(params?.page) || Number(meta?.currentPage) || 1;

  const {
    expanded,
    setExpanded,
    scaleFactor,
    setScaleFactor,
    selectedDataSource,
    setSelectedDataSource,
    cloneSelectedFlow,
    setCloneSelectedFlow,
    currentPage,
    setCurrentPage,
    sortCriteria,
    setSortCriteria,
  } = useUncategorizedPipelinesState({
    initialPage,
    cleanup: cleanupFlowsWithoutProjects,
  });

  const {
    toggleResourceId,
    selectedResourceIds,
    resetSelectedResourceIds,
    selectAllResourceIds,
    deselectAllResourceIds,
    isResourceIdSelected,
  } = useSelectedResourceIds();

  const location = useLocation();

  const listRef = useRef<any>(null);
  const searchBarRef = useRef<any>(null);
  const sourcePipelineElementRef = useRef<HTMLElement | null>(null);

  const callAction = useCallback(() => {
    requestFlowsWithoutProjects({
      page: currentPage,
      per_page: ITEMS_PER_PAGE,
      sort: {
        sortKey: Constants.SortCriteriaInfo[sortCriteria]?.sortKey || 'created_at',
        sortOrder: Constants.SortCriteriaInfo[sortCriteria]?.sortOrder || 'desc',
      },
    });
  }, [filters, currentPage, sortCriteria, accessControlMode, requestFlowsWithoutProjects]);

  useEffect(() => {
    callAction();
  }, [callAction]);

  useEffect(() => {
    resetSelectedResourceIds(); // reset selected IDs when page changes
  }, [currentPage, resetSelectedResourceIds]);

  useEffect(() => {
    resetSelectedResourceIds();
  }, [accessControlMode, resetSelectedResourceIds]);

  /**
   * @description Map flow IDs to flow entities
   */
  const pipelines = useMemo(() => {
    if (!flowIds || flowIds.length === 0) return [];

    return map(flowIds, (flowNodeId) => {
      const flowNode = flowNodes[flowNodeId];
      if (!flowNode || flowNode.project_id !== null) return null;

      const MERGE = true;
      return getResourceEntity(flowNode, entities, MERGE, ['name', 'description']);
    }).filter(Boolean);
  }, [flowIds, flowNodes, entities]);

  const paginatedPipelines = pipelines;

  const handleAutoRedirect = useCallback(
    (page: number) => {
      setCurrentPage(page);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set('page', String(page));
      history.replace(`${location.pathname}?${searchParams.toString()}`);
    },
    [setCurrentPage, history, location.pathname, location.search],
  );

  /**
   * Auto-redirect when page becomes empty after assignment
   */
  useAutoRedirectOnEmptyPage({
    currentPage,
    items: paginatedPipelines,
    meta,
    isLoading: isLoadingPipelines,
    isAssigning: isAssigningLoading,
    onNavigate: handleAutoRedirect,
  });

  const totalPages = useMemo(() => {
    return meta?.totalPages || 1;
  }, [meta?.totalPages]);

  // Get source meta for SearchBar
  const getSourceMeta = useCallback(
    (flowNodeId: number) => {
      const sourceId = flowNodes[flowNodeId]?.data_source_id;
      const connectionOrVendorType = getConnectionOrVendorType(
        sources[sourceId],
        vendors,
        'source_type',
        'vendor_id',
      );

      if (connectionOrVendorType && has(pagingMetaInfo, connectionOrVendorType)) {
        return pagingMetaInfo[connectionOrVendorType];
      }

      return null;
    },
    [flowNodes, sources, vendors, pagingMetaInfo],
  );

  const handleSourceClicked = useCallback(
    (source: any) => {
      const isCollapsing = !!selectedDataSource && source.nodeKey === selectedDataSource;
      // Toggle immediately so a collapse click is as responsive as expand
      // (previously a 1s deferred shrink animation made it feel like nothing happened).
      setSelectedDataSource(isCollapsing ? null : source?.flow_node_id);
    },
    [selectedDataSource],
  );

  const handleRefreshPipeline = useCallback(() => {
    if (!selectedDataSource) return;

    const source = getResourceEntity(flowNodes[selectedDataSource], entities);
    const sourceExists = pipelines.find((p: any) => p.flow_node_id === selectedDataSource);

    if (
      selectedDataSource &&
      sourceExists &&
      !isRefreshingPipeline &&
      source?.dataSetType !== 'shared'
    ) {
      dispatch(refreshPipeline(selectedDataSource));
    }
  }, [
    selectedDataSource,
    expanded,
    isRefreshingPipeline,
    flowNodes,
    entities,
    pipelines,
    dispatch,
  ]);

  const handleExpandFlow = useCallback(
    (flowId: number) => {
      const flow = flowNodes[flowId];
      if (flow?.flow_type === 'rag') {
        history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/${flowId}/edit`);
      } else {
        history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/${flow?.flow_type}/${flowId}/edit`);
      }
    },
    [history, flowNodes],
  );

  const handleSearchSuggestionSelected = useCallback(
    (flowNodeId: number) => {
      const selectedFlow = getResourceEntity(flowNodes[flowNodeId], entities);
      setSelectedDataSource(selectedFlow?.flow_node_id);

      const index = pipelines.findIndex((p: any) => p.id === flowNodeId);
      if (index !== -1 && listRef.current) {
        listRef.current.scrollToIndex({
          index,
          align: 'start',
          offset: -60,
        });
      }
    },
    [flowNodes, entities, pipelines],
  );

  const handleSortCriteriaChanged = useCallback((selectedOption: any) => {
    const value = getNewSelectionValue(selectedOption, true, '');
    setSortCriteria(value);
    setCurrentPage(1);
  }, []);

  const handleCloseSourcePipeline = useCallback(() => {
    setSelectedDataSource(null);
  }, []);

  const toggleCloneFlowModal = useCallback((data: any = null) => {
    setCloneSelectedFlow(data);
  }, []);

  const toggleUseExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const handleScaleFactorChanged = useCallback((newScaleFactor: number) => {
    setScaleFactor(newScaleFactor);
  }, []);

  const onRootRef = useCallback((instance: HTMLElement) => {
    if (instance && instance !== sourcePipelineElementRef.current) {
      sourcePipelineElementRef.current = instance;
    }
  }, []);

  const selectedSortOption = useMemo(() => {
    return getCurrentSelectedOption(sortCriteriaOptions, sortCriteria);
  }, [sortCriteria]);

  const isLoading = (isLoadingPipelines && !isRefreshingPipeline) || loading;

  const showEmptyState = !loading && !isLoadingPipelines && isEmpty(paginatedPipelines);
  const showNoResults = false; // @SearchBar handles its own empty state

  return (
    <div>
      <section className="view-pipelines">
        <div className="list-controls">
          <MacroOptionsContextWrap>
            <SearchBar
              ref={searchBarRef}
              isClientSideSearch
              view={SEARCH_VIEW}
              availableFilterTypes={AVAILABLE_TYPES}
              getSourceMeta={getSourceMeta}
              onResourceSelected={handleSearchSuggestionSelected}
              onLoadPipelinesSearchSuggestions={() => {}} // suppress the default loadPipelinesSearchSuggestions
            />
          </MacroOptionsContextWrap>
          <div className="control-buttons">
            <UnsortedSelect
              isSearchable
              isClearable={false}
              backspaceRemovesValue={false}
              options={sortCriteriaOptions}
              value={selectedSortOption}
              onChange={handleSortCriteriaChanged}
              styles={selectStyles}
              components={{ Input: SelectInput, Option: SelectOption }}
            />
            <CardsListPageNumbers
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={meta?.totalItems}
              isLoading={isLoadingPipelines}
              onChange={setCurrentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>

        <BulkActionsBar
          sources={paginatedPipelines}
          selectedIds={selectedResourceIds}
          isAssigningLoading={isAssigningLoading}
          assignContextView="uncategorized_pipelines"
          onSelectAll={selectAllResourceIds}
          onDeselectAll={deselectAllResourceIds}
          onActionComplete={() => {
            resetSelectedResourceIds();
            callAction();
          }}
        />

        <div
          className={`view-filter-pipeline-results ${isEmpty(paginatedPipelines) && !isLoading ? 'no-pipelines' : ''}`}
          style={!isEmpty(paginatedPipelines) && !isLoading ? { paddingRight: 8 } : {}}
        >
          <div className="content-container">
            <div className="sources-list">
              <PipelinesList
                ref={listRef}
                // @ts-ignore
                isLoading={isLoading}
                onClickExpandFlow={handleExpandFlow}
                isOldDesign={false}
                _handleSourceClicked={handleSourceClicked}
                isRefreshingPipeline={isRefreshingPipeline}
                selectedDataSource={selectedDataSource}
                scaleFactor={scaleFactor}
                _sourcePipelineElement={sourcePipelineElementRef.current}
                sources={paginatedPipelines}
                onRootRef={onRootRef}
                _toggleCloneFlowModal={toggleCloneFlowModal}
                _handleScaleFactorChanged={handleScaleFactorChanged}
                refreshPipeline={handleRefreshPipeline}
                _handleCloseSourcePipeline={handleCloseSourcePipeline}
                toggleUseExpanded={toggleUseExpanded}
                onItemCheckboxChange={toggleResourceId}
                isItemCheckboxChecked={isResourceIdSelected}
              />
            </div>

            {selectedCardID && (
              <FlowDetailViewContainer
                key={selectedCardID}
                toggleCloneFlowModal={toggleCloneFlowModal}
              />
            )}

            {showNoResults && (
              <NoContentTextView title={<div>No results match your search criteria.</div>} />
            )}

            {showEmptyState && !showNoResults && (
              <NoContentTextView
                title={<div>All your flows have been assigned to projects.</div>}
              />
            )}

            {cloneSelectedFlow && (
              <ModalCloneFlowContainer
                data={cloneSelectedFlow}
                toggleCloneFlowModal={toggleCloneFlowModal}
                setSelectedPipeline={setSelectedDataSource}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default UncategorizedPipelines;
